import { useEffect } from 'react';

interface ServiceSchemaProps {
  serviceName: string;
  description: string;
  priceRange?: string;
  serviceArea: string[];
  typicalDuration?: string;
  serviceType: string;
  provider: {
    name: string;
    telephone: string;
    address: {
      streetAddress: string;
      addressLocality: string;
      addressRegion: string;
      postalCode: string;
    };
  };
}

export default function ServiceSchema({
  serviceName,
  description,
  priceRange = "$$",
  serviceArea,
  typicalDuration,
  serviceType,
  provider
}: ServiceSchemaProps) {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': serviceName,
      'description': description,
      'provider': {
        '@type': 'LocalBusiness',
        'name': provider.name,
        'telephone': provider.telephone,
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': provider.address.streetAddress,
          'addressLocality': provider.address.addressLocality,
          'addressRegion': provider.address.addressRegion,
          'postalCode': provider.address.postalCode,
          'addressCountry': 'US'
        },
        'hasCredential': {
          '@type': 'EducationalOccupationalCredential',
          'credentialCategory': 'license',
          'name': 'California Contractor License',
          'identifier': '662554',
          'recognizedBy': {
            '@type': 'Organization',
            'name': 'California Contractors State License Board'
          }
        },
        'additionalProperty': [
          {
            '@type': 'PropertyValue',
            'name': 'License Number',
            'value': 'LIC #662554'
          },
          {
            '@type': 'PropertyValue',
            'name': 'Issuing Authority',
            'value': 'California Contractors State License Board (CSLB)'
          }
        ]
      },
      'serviceType': serviceType,
      'areaServed': serviceArea.map(area => ({
        '@type': 'City',
        'name': area
      })),
      'priceRange': priceRange,
      ...(typicalDuration && { 'estimatedDuration': typicalDuration }),
      'offers': {
        '@type': 'Offer',
        'priceSpecification': {
          '@type': 'PriceSpecification',
          'priceCurrency': 'USD'
        },
        'availability': 'https://schema.org/InStock'
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [serviceName, description, priceRange, serviceArea, typicalDuration, serviceType, provider]);

  return null;
}
